/**
 * As quatro listas de domínio do `cadastro` (FR-66, FR-67 emendado) — repartidas entre as abas
 * Negócios (Motivo de perda) e Clientes (Origem do cliente, Categoria, Setor) da Configuração,
 * pela prop `listas`. Desde a 11.3 Sub setor saiu e "Origem do negócio" virou "Origem do
 * cliente" (a Origem mora na Empresa; o Negócio herda). A quinta lista do FR-67, a Previsão de
 * fechamento, mora no `negocios` e tem cartão próprio (`PrevisaoDeFechamento`).
 *
 * **Um cartão por lista, com os valores em chips — o desenho do protótipo v2 (28/09/2026).** Até
 * 27/09 era um cartão-índice ("Motivo de perda · 2 valores · Editar") que abria um painel; o
 * Gustavo comparou com o protótipo e pediu o desenho dele: cada lista é um cartão com título,
 * nota, os valores em chips e o chip tracejado **"+ adicionar"**, que abre o campo do valor novo
 * ali mesmo. **"Editar"**, no cabeçalho, troca o cartão para o modo de edição, em que cada valor
 * vira uma linha com renomear e excluir — e, no mesmo modo, o formulário de cadastro. Um cartão em
 * edição por vez: abrir outro fecha o anterior, com o rascunho, a linha aberta e a exclusão a
 * confirmar **daquela** lista.
 *
 * **Sem cabeçalho próprio.** O `Cabecalho` do shell já desenha o título e o subtítulo do item
 * de navegação; um `<h1>` aqui daria dois títulos na mesma tela.
 *
 * **A ordem das quatro é a do protótipo**, e não a alfabética nem a do enum: Motivo de perda e
 * Origem do cliente primeiro, porque são os dois cujo vazio **bloqueia** alguma coisa — o
 * Motivo de perda trava o "marcar perdido" do FR-9, e a Origem deixa o relatório sem
 * agrupamento. A lista vazia mostra o aviso vermelho "vazia" com o texto literal do protótipo.
 *
 * **Excluir é em dois passos.** "Excluir" abre "Confirmar exclusão"/"Cancelar" na própria
 * linha: o valor sai das escolhas novas de toda ficha da Empresa, e um clique só, sem volta,
 * numa lista de rótulos curtos e parecidos é a receita de excluir o vizinho. O serviço exclui
 * **logicamente** (FR-67) — o valor continua nos registros antigos —, mas quem está na tela
 * não vê isso: para ele, o valor sumiu.
 *
 * **Tudo o que o servidor recusar aparece verbatim.** A recusa dele é a que explica o caso —
 * o `403` de quem não é gestão, o `409` do rótulo repetido — e reescrevê-la aqui seria uma
 * segunda fonte de verdade que envelhece calada.
 *
 * **Esconder não é autorizar, e continua não sendo.** A tela mostra cadastrar, editar e
 * excluir sem consultar o perfil de quem entrou; quem recusa é o serviço, e a mensagem dele
 * vai à tela verbatim. É a convenção desde a 2.1 — o `FormularioDeEmpresa` e a seção de
 * Responsáveis fazem o mesmo.
 *
 * **Depois de cada mutação o vocabulário é relido do serviço**, e não remendado com a
 * resposta: a mesma chave de cache alimenta os seletores da ficha da Empresa, e a resposta de
 * uma mutação é **uma** opção, não as quatro listas.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop e não há árvore
 * alternativa: os chips quebram linha sozinhos, e cada linha da edição empilha. Sem `@media`.
 */

import { type FormEvent, useState } from "react";

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from "@/api/erros";
import {
  type OpcaoDeDominio,
  type TipoDeLista,
  useCadastrarOpcao,
  useEditarOpcao,
  useExcluirOpcao,
  useOpcoesDeDominio,
} from "./api";
import "./listas-de-dominio.css";

/**
 * O teto de coluna do serviço (`servicos/cadastro/dominio/opcao_de_dominio.py`), pela mesma
 * regra do formulário da ficha: o servidor continua sendo quem decide, e o `maxLength` só
 * evita gastar uma ida à rede com o que o cliente já sabe.
 */
const TAMANHO_DO_ROTULO = 120;

/**
 * Nome e status de cada lista — **literais do protótipo aprovado**.
 *
 * `Record<TipoDeLista, …>` é o guarda de exaustividade: ele exige as quatro chaves do contrato
 * gerado e recusa qualquer uma que não seja dele, então lista nova no backend, ou lista
 * renomeada, quebra o build aqui — em vez de a linha sumir da tela em silêncio.
 */
export const LISTAS: Record<
  TipoDeLista,
  { nome: string; nota: string; statusVazia: string }
> = {
  motivo_de_perda: {
    nome: "Motivos de perda",
    nota:
      "Obrigatório ao marcar um negócio como perdido — o botão só libera com um motivo " +
      "escolhido. A descrição é opcional.",
    statusVazia: "vazia · bloqueia marcar perdido",
  },
  origem_do_cliente: {
    nome: "Origem do cliente",
    nota:
      "De onde o cliente veio. Os negócios herdam a origem do cliente — um cliente pode ter " +
      "vários negócios com a mesma origem.",
    statusVazia: "vazia · relatório sem agrupamento",
  },
  categoria: {
    nome: "Categorias",
    nota: "Classificação da empresa na ficha.",
    statusVazia: "vazia",
  },
  setor: {
    nome: "Setores",
    nota: "O setor de atuação da empresa.",
    statusVazia: "vazia",
  },
};

/**
 * A ordem em que as quatro aparecem — a do protótipo, e não a do enum nem a alfabética.
 *
 * As duas primeiras são as que bloqueiam alguma coisa quando vazias; ordenar por outra coisa
 * enterraria justamente as que precisam ser preenchidas antes do Épico 3.
 */
const ORDEM_DAS_LISTAS: readonly TipoDeLista[] = [
  "motivo_de_perda",
  "origem_do_cliente",
  "categoria",
  "setor",
];

const FALTA_ROTULO = "Informe o rótulo do valor.";

/** Confirmações. Sem elas a pessoa reenvia por não saber se gravou. */
const CADASTRADO = "Valor cadastrado.";

/**
 * Restaurar não é cadastrar, e a confirmação diz qual dos dois aconteceu.
 *
 * Quando o rótulo já existia **excluído**, o serviço reativa a linha antiga: o `id` é o mesmo,
 * e portanto toda ficha que já apontava para ela volta a apontar para uma opção viva. Dizer
 * "Valor cadastrado." ali esconderia justamente o que a pessoa precisa saber — que ela não
 * criou um valor novo, e sim trouxe de volta um que estava em uso.
 */
const RESTAURADO =
  "Valor restaurado — ele já existia nesta lista e estava excluído.";

const SALVO = "Valor salvo.";
const EXCLUIDO = "Valor excluído.";

const CARREGANDO = "Carregando as listas…";
const SEM_VALORES = "Esta lista ainda não tem valores cadastrados.";

/** O nome acessível da `<ul>` do índice — a que lista as quatro, e não os valores de uma. */
const NOME_DO_INDICE = "As quatro listas de domínio";

/** `id` do painel de edição de uma lista — é ele que o `aria-controls` do "Editar" aponta. */
function idDoPainel(tipo: TipoDeLista): string {
  return `listas-painel-${tipo}`;
}

/** O nome acessível da `<section>` — a região que reúne os cartões desta instância. */
const NOME_DA_SECAO = "Listas de domínio";

/** O chip tracejado do protótipo, que abre o campo do valor novo ali mesmo. */
const ADICIONAR = "+ adicionar";

/** `id` da recusa local do formulário de cadastro, para o `aria-describedby` do Rótulo. */
const ID_DA_RECUSA_DO_CADASTRO = "listas-recusa-novo";

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi
    ? falha.message
    : MENSAGEM_DE_ERRO_DESCONHECIDO;
}

function CampoDeRotulo({
  id,
  valor,
  aoTrocar,
  recusadoPor,
}: {
  id: string;
  valor: string;
  aoTrocar: (valor: string) => void;
  /**
   * `id` da mensagem que recusou **este** campo, quando há uma.
   *
   * É o que liga a recusa ao campo que ela nomeia: sem `aria-invalid` e `aria-describedby`,
   * quem usa leitor de tela ouve "Informe o rótulo do valor" solto e precisa adivinhar de
   * qual dos formulários da tela a mensagem fala.
   */
  recusadoPor?: string | null;
}) {
  return (
    <div className="listas__campo">
      <label className="listas__rotulo" htmlFor={id}>
        Rótulo
      </label>
      <input
        id={id}
        className="listas__entrada"
        type="text"
        value={valor}
        maxLength={TAMANHO_DO_ROTULO}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={recusadoPor ?? undefined}
        onChange={(evento) => {
          aoTrocar(evento.target.value);
        }}
      />
    </div>
  );
}

/** Uma linha do painel: o rótulo, e as duas ações — com a exclusão em dois passos. */
function Valor({
  opcao,
  emEdicao,
  edicaoAberta,
  aConfirmar,
  aoEditar,
  aoCancelarEdicao,
  aoSalvar,
  aoPedirExclusao,
  aoCancelarExclusao,
  aoExcluir,
  ocupado,
  salvando,
}: {
  opcao: OpcaoDeDominio;
  emEdicao: boolean;
  /** Alguma linha do painel está aberta em edição — não necessariamente esta. */
  edicaoAberta: boolean;
  /** Esta linha pediu exclusão e espera a confirmação. */
  aConfirmar: boolean;
  aoEditar: () => void;
  aoCancelarEdicao: () => void;
  aoSalvar: (rotulo: string) => void;
  aoPedirExclusao: () => void;
  aoCancelarExclusao: () => void;
  aoExcluir: () => void;
  ocupado: boolean;
  /** A **edição desta linha** está em voo — e não qualquer escrita da tela. */
  salvando: boolean;
}) {
  const [rascunho, setRascunho] = useState(opcao.rotulo);
  const [recusa, setRecusa] = useState<string | null>(null);
  const idDaRecusa = `listas-recusa-${opcao.id}`;

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (rascunho.trim() === "") {
      setRecusa(FALTA_ROTULO);
      return;
    }
    aoSalvar(rascunho.trim());
  }

  if (emEdicao) {
    return (
      <li className="listas__item">
        <form className="listas__formulario" onSubmit={enviar} noValidate>
          <CampoDeRotulo
            id={`opcao-rotulo-${opcao.id}`}
            valor={rascunho}
            recusadoPor={recusa === null ? null : idDaRecusa}
            aoTrocar={(valor) => {
              setRecusa(null);
              setRascunho(valor);
            }}
          />
          {recusa === null ? null : (
            <p className="listas__erro" id={idDaRecusa} role="alert">
              {recusa}
            </p>
          )}
          <div className="listas__acoes">
            {/* O rótulo segue a **edição desta linha**, e o `disabled` segue a tela inteira:
                cadastrar noutro formulário ocupa a linha, mas não é ela que está gravando — e
                um botão que diz "Salvando…" sem estar salvando mente para o leitor de tela. */}
            <button className="listas__botao" type="submit" disabled={ocupado}>
              {salvando ? "Salvando…" : "Salvar valor"}
            </button>
            <button
              className="listas__botao listas__botao--discreto"
              type="button"
              disabled={ocupado}
              aria-label={`Cancelar a edição de ${opcao.rotulo}`}
              onClick={() => {
                // O rascunho volta ao que está gravado: sem isto, cancelar e reabrir mostraria
                // de novo o que a pessoa acabou de decidir descartar.
                setRascunho(opcao.rotulo);
                setRecusa(null);
                aoCancelarEdicao();
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="listas__item">
      <div className="listas__linha">
        <span className="listas__valor">{opcao.rotulo}</span>
        <div className="listas__acoes">
          {aConfirmar ? (
            <>
              {/*
               * O segundo passo. O nome do valor vai no `aria-label` porque numa lista de
               * rótulos curtos e parecidos um "Confirmar exclusão" solto não diz **qual**
               * some — e o que some sai das escolhas novas de toda ficha da Empresa.
               */}
              <button
                className="listas__botao listas__botao--perigo"
                type="button"
                disabled={ocupado}
                aria-label={`Confirmar a exclusão de ${opcao.rotulo}`}
                onClick={aoExcluir}
              >
                Confirmar exclusão
              </button>
              <button
                className="listas__botao listas__botao--discreto"
                type="button"
                disabled={ocupado}
                aria-label={`Cancelar a exclusão de ${opcao.rotulo}`}
                onClick={aoCancelarExclusao}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              {/*
               * "Editar" fica desabilitado enquanto alguma linha está aberta: abrir a segunda
               * não fecha a primeira, e a primeira continua montada com o rascunho digitado no
               * estado. Reabrindo-a, o texto abandonado voltaria — exatamente o que "Cancelar"
               * existe para impedir. Uma linha aberta por vez.
               */}
              <button
                className="listas__botao listas__botao--discreto"
                type="button"
                disabled={ocupado || edicaoAberta}
                aria-label={`Editar ${opcao.rotulo}`}
                onClick={aoEditar}
              >
                Editar
              </button>
              <button
                className="listas__botao listas__botao--discreto"
                type="button"
                disabled={ocupado || edicaoAberta}
                aria-label={`Excluir ${opcao.rotulo}`}
                onClick={aoPedirExclusao}
              >
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * O formulário do valor novo — o mesmo no modo de edição e atrás do chip "+ adicionar".
 *
 * O `<h4>` "Cadastrar valor" o nomeia; `aoCancelar` só existe atrás do chip, onde fechar o campo
 * é um gesto que faz sentido (no modo de edição ele é parte do painel).
 */
function FormularioDeCadastro({
  nome,
  rascunho,
  recusa,
  ocupado,
  cadastrando,
  aoTrocar,
  aoEnviar,
  aoCancelar,
}: {
  nome: string;
  rascunho: string;
  recusa: string | null;
  ocupado: boolean;
  cadastrando: boolean;
  aoTrocar: (valor: string) => void;
  aoEnviar: (evento: FormEvent<HTMLFormElement>) => void;
  aoCancelar?: () => void;
}) {
  return (
    <form className="listas__formulario" onSubmit={aoEnviar} noValidate>
      <h4 className="listas__subtitulo">Cadastrar valor</h4>
      <CampoDeRotulo
        id="opcao-rotulo-novo"
        valor={rascunho}
        recusadoPor={recusa === null ? null : ID_DA_RECUSA_DO_CADASTRO}
        aoTrocar={aoTrocar}
      />
      {recusa === null ? null : (
        <p className="listas__erro" id={ID_DA_RECUSA_DO_CADASTRO} role="alert">
          {recusa}
        </p>
      )}
      <div className="listas__acoes">
        <button className="listas__botao" type="submit" disabled={ocupado}>
          {cadastrando ? "Cadastrando…" : "Cadastrar valor"}
        </button>
        {aoCancelar === undefined ? null : (
          <button
            className="listas__botao listas__botao--discreto"
            type="button"
            disabled={ocupado}
            aria-label={`Cancelar o cadastro em ${nome}`}
            onClick={aoCancelar}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * As listas desta instância do cartão, na ordem do protótipo.
 *
 * **Desde a 11.2 o cartão é repartido pelas abas da Configuração** — Motivo de perda em Negócios;
 * Origem do cliente, Categoria e Setor em Clientes —, e a repartição é **só de
 * exibição**: a lógica de cadastrar, editar e excluir é a mesma para qualquer subconjunto. Sem
 * `listas`, o cartão mostra as quatro, como sempre mostrou. A 11.3 tirou Sub setor e renomeou a Origem.
 */
export function ListasDeDominio({
  listas = ORDEM_DAS_LISTAS,
}: { listas?: readonly TipoDeLista[] } = {}) {
  const exibidas = ORDEM_DAS_LISTAS.filter((tipo) => listas.includes(tipo));
  const todas = exibidas.length === ORDEM_DAS_LISTAS.length;
  const consulta = useOpcoesDeDominio();
  const cadastro = useCadastrarOpcao();
  const edicao = useEditarOpcao();
  const exclusao = useExcluirOpcao();

  /** O cartão em modo de edição — um por vez. */
  const [listaAberta, setListaAberta] = useState<TipoDeLista | null>(null);
  /** O cartão cujo chip "+ adicionar" abriu o campo do valor novo — também um por vez. */
  const [cadastrandoEm, setCadastrandoEm] = useState<TipoDeLista | null>(null);
  const [rascunho, setRascunho] = useState("");
  const [emEdicao, setEmEdicao] = useState<string | null>(null);
  const [aConfirmar, setAConfirmar] = useState<string | null>(null);
  const [recusaDoCadastro, setRecusaDoCadastro] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<string | null>(null);

  const ocupado = cadastro.isPending || edicao.isPending || exclusao.isPending;
  const opcoes = consulta.data;
  /** A lista que o formulário de cadastro alimenta: a em edição, ou a do chip. */
  const alvoDoCadastro = listaAberta ?? cadastrandoEm;

  /**
   * Os avisos falam do envio que está em voo — nunca do anterior.
   *
   * Sem isto, a recusa de uma gravação continua na tela enquanto a próxima corre, e como o
   * `role="status"` da confirmação só aparece com `erro === null`, o sucesso seguinte ficaria
   * mudo até que a mudança de estado o limpasse. **Os três**, e não dois: a recusa local do
   * cadastro só se apaga sozinha na tecla seguinte do próprio formulário.
   */
  function limparOsAvisos() {
    setErro(null);
    setRecusaDoCadastro(null);
    setConfirmacao(null);
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null);
    setErro(mensagemDe(falha));
  }

  /** Zera o que era **daquele** cartão: rascunho, linha aberta, exclusão a confirmar e avisos. */
  function zerar() {
    setRascunho("");
    setEmEdicao(null);
    setAConfirmar(null);
    limparOsAvisos();
  }

  function abrir(tipo: TipoDeLista) {
    // Trocar de lista zera o painel inteiro: o rascunho, a linha aberta e a exclusão que
    // esperava confirmação são **daquela** lista, e carregá-los para a próxima faria a
    // confirmação de um clique valer para um valor que a pessoa nem está mais vendo.
    setListaAberta(tipo);
    setCadastrandoEm(null);
    zerar();
  }

  function fechar() {
    setListaAberta(null);
    zerar();
  }

  function abrirCadastro(tipo: TipoDeLista) {
    setListaAberta(null);
    setCadastrandoEm(tipo);
    zerar();
  }

  function fecharCadastro() {
    setCadastrandoEm(null);
    zerar();
  }

  function cadastrar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (alvoDoCadastro === null) {
      return;
    }
    if (rascunho.trim() === "") {
      // Limpa **antes** de recusar: sem isto a tela mostra "Valor cadastrado." (da gravação
      // anterior) e "Informe o rótulo do valor." lado a lado, e a confirmação passa a
      // testemunhar um envio que não aconteceu.
      limparOsAvisos();
      setRecusaDoCadastro(FALTA_ROTULO);
      return;
    }
    limparOsAvisos();
    cadastro.mutate(
      { tipo: alvoDoCadastro, rotulo: rascunho.trim() },
      {
        onSuccess: ({ restaurada }) => {
          setErro(null);
          setRecusaDoCadastro(null);
          setConfirmacao(restaurada ? RESTAURADO : CADASTRADO);
          setRascunho("");
        },
        onError: aoFalhar,
      },
    );
  }

  function salvar(opcaoId: string, rotulo: string) {
    limparOsAvisos();
    edicao.mutate(
      { opcaoId, opcao: { rotulo } },
      {
        onSuccess: () => {
          setErro(null);
          setConfirmacao(SALVO);
          setEmEdicao(null);
        },
        onError: aoFalhar,
      },
    );
  }

  function excluir(opcaoId: string) {
    limparOsAvisos();
    exclusao.mutate(opcaoId, {
      onSuccess: () => {
        setErro(null);
        setConfirmacao(EXCLUIDO);
        setAConfirmar(null);
      },
      onError: aoFalhar,
    });
  }

  const avisos = (
    <>
      {erro === null ? null : (
        <p className="listas__erro" role="alert">
          {erro}
        </p>
      )}
      {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
          interromper o que a pessoa estiver fazendo para dizer isso. */}
      {confirmacao === null || erro !== null ? null : (
        <p className="listas__sucesso" role="status">
          {confirmacao}
        </p>
      )}
    </>
  );

  function formulario(tipo: TipoDeLista, comCancelar: boolean) {
    return (
      <FormularioDeCadastro
        nome={LISTAS[tipo].nome}
        rascunho={rascunho}
        recusa={recusaDoCadastro}
        ocupado={ocupado}
        cadastrando={cadastro.isPending}
        aoTrocar={(valor) => {
          // Qualquer alteração invalida a confirmação **e** as recusas anteriores: os
          // avisos falam do que está no servidor agora, e não do que estava antes.
          limparOsAvisos();
          setRascunho(valor);
        }}
        aoEnviar={cadastrar}
        aoCancelar={comCancelar ? fecharCadastro : undefined}
      />
    );
  }

  /** O painel de edição de uma lista: as linhas com renomear/excluir e o cadastro. */
  function painelDeEdicao(tipo: TipoDeLista, valores: readonly OpcaoDeDominio[]) {
    return (
      <div className="listas__corpo" id={idDoPainel(tipo)}>
        <span className="listas__ajuda">
          Excluir um valor o tira das escolhas novas. Os registros que já o usam continuam
          exibindo o mesmo rótulo.
        </span>
        {valores.length === 0 ? (
          <p className="listas__ajuda">{SEM_VALORES}</p>
        ) : (
          <ul className="listas__lista" aria-label={`Valores de ${LISTAS[tipo].nome}`}>
            {valores.map((opcao) => (
              <Valor
                // `key` com o rótulo junto: a linha guarda o rascunho da edição no estado,
                // e sem a remontagem ele atravessaria uma releitura da lista — reabrir
                // "Editar" mostraria o texto velho, e salvar desfaria a alteração de outra
                // pessoa. Mesma regra da seção de Responsáveis (2.2).
                key={`${opcao.id}-${opcao.rotulo}`}
                opcao={opcao}
                emEdicao={emEdicao === opcao.id}
                edicaoAberta={emEdicao !== null}
                aConfirmar={aConfirmar === opcao.id}
                ocupado={ocupado}
                salvando={edicao.isPending && emEdicao === opcao.id}
                aoEditar={() => {
                  limparOsAvisos();
                  setAConfirmar(null);
                  setEmEdicao(opcao.id);
                }}
                aoCancelarEdicao={() => {
                  // Cancelar também limpa: uma recusa do servidor continuaria visível sem
                  // nada pendente na tela que a explicasse — e a confirmação seguinte
                  // ficaria muda, porque ela só aparece com `erro === null`.
                  limparOsAvisos();
                  setEmEdicao(null);
                }}
                aoSalvar={(rotulo) => {
                  salvar(opcao.id, rotulo);
                }}
                aoPedirExclusao={() => {
                  limparOsAvisos();
                  setAConfirmar(opcao.id);
                }}
                aoCancelarExclusao={() => {
                  limparOsAvisos();
                  setAConfirmar(null);
                }}
                aoExcluir={() => {
                  excluir(opcao.id);
                }}
              />
            ))}
          </ul>
        )}
        {formulario(tipo, false)}
        {avisos}
      </div>
    );
  }

  /** O corpo de leitura: os valores em chips e o "+ adicionar" (protótipo v2). */
  function chips(tipo: TipoDeLista, valores: readonly OpcaoDeDominio[]) {
    const cadastrando = cadastrandoEm === tipo;
    return (
      <>
        <div className="listas__chips">
          {valores.length === 0 ? (
            <span className="listas__chip listas__chip--vazia">{LISTAS[tipo].statusVazia}</span>
          ) : (
            valores.map((opcao) => (
              <span className="listas__chip" key={opcao.id}>
                {opcao.rotulo}
              </span>
            ))
          )}
          <button
            className="listas__chip listas__chip--tracejado"
            type="button"
            aria-label={`Adicionar valor em ${LISTAS[tipo].nome}`}
            aria-expanded={cadastrando}
            onClick={() => {
              if (cadastrando) {
                fecharCadastro();
              } else {
                abrirCadastro(tipo);
              }
            }}
          >
            {ADICIONAR}
          </button>
        </div>
        {cadastrando ? (
          <div className="listas__corpo">
            {formulario(tipo, true)}
            {avisos}
          </div>
        ) : null}
      </>
    );
  }

  return (
    <section className="listas" aria-label={NOME_DA_SECAO}>
      {consulta.isError ? (
        <div className="listas__cartao">
          <div className="listas__corpo">
            {/* Verbatim: a recusa do serviço é a que sabe explicar o caso. */}
            <p className="listas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          </div>
        </div>
      ) : /* `|| opcoes === undefined` e o que fecha o terceiro estado: sem rede, a consulta
             fica **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela
             desenharia as quatro listas como "vazia", afirmando ausencia onde so ha
             desconhecimento. A gestao leria "nenhum motivo de perda cadastrado" de uma
             lista cheia. */
      consulta.isLoading || opcoes === undefined ? (
        <div className="listas__cartao">
          <div className="listas__corpo">
            <p className="listas__ajuda" role="status">
              {CARREGANDO}
            </p>
          </div>
        </div>
      ) : (
        /* A `<ul>` dos cartões ganha nome próprio: há a lista de valores dentro de cada um, e
           sem ele o leitor de tela anuncia "lista" duas vezes e quem navega por marcos não sabe
           em qual entrou. */
        <ul
          className="listas__cartoes"
          aria-label={
            todas
              ? NOME_DO_INDICE
              : `Listas de domínio: ${exibidas.map((tipo) => LISTAS[tipo].nome).join(", ")}`
          }
        >
          {exibidas.map((tipo) => {
            const aberta = listaAberta === tipo;
            const valores = opcoes[tipo];
            return (
              <li className="listas__cartao" key={tipo}>
                <div className="listas__cabecalho listas__cabecalho--linha">
                  <div className="listas__cabecalho-texto">
                    <h2 className="listas__titulo">{LISTAS[tipo].nome}</h2>
                    <span className="listas__ajuda">{LISTAS[tipo].nota}</span>
                  </div>
                  {/*
                   * "Editar" é o rótulo do protótipo. O nome da lista vai no `aria-label`: são
                   * quatro botões iguais, e sem ele o leitor de tela anunciaria quatro "Editar"
                   * indistinguíveis. `aria-expanded` + `aria-controls`: o botão revela o painel de
                   * edição, e clicar de novo o fecha.
                   */}
                  <button
                    className="listas__acao"
                    type="button"
                    aria-label={`Editar a lista ${LISTAS[tipo].nome}`}
                    aria-expanded={aberta}
                    aria-controls={aberta ? idDoPainel(tipo) : undefined}
                    onClick={() => {
                      if (aberta) {
                        fechar();
                      } else {
                        abrir(tipo);
                      }
                    }}
                  >
                    {aberta ? "Concluir" : "Editar"}
                  </button>
                </div>
                {aberta ? painelDeEdicao(tipo, valores) : chips(tipo, valores)}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
